import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, MessageCircle } from "lucide-react";
import { services } from "../../data/services";

const mainNav = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/expertise", label: "Expertise", hasMega: true },
  { to: "/principal-advocate", label: "Principal Advocate" },
  { to: "/insights", label: "Insights" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpertiseOpen, setMobileExpertiseOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setMegaOpen(false);
    setMobileExpertiseOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-ivory/95 backdrop-blur-md border-b border-soft-grey"
          : "bg-ivory/80 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="container-px max-w-[1400px] mx-auto flex items-center justify-between gap-6 h-20 md:h-24">
        {/* Logo */}
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex items-center gap-3 group flex-shrink-0"
          aria-label="Lex Polaris home"
        >
          <img
            src="/brand/lex-polaris-icon.png"
            alt="Lex Polaris"
            width="44"
            height="44"
            className="w-10 h-10 md:w-11 md:h-11 object-contain shadow-sm"
            data-testid="header-logo-icon"
          />
          <span className="flex flex-col leading-none">
            <span className="font-serif text-[22px] md:text-[26px] tracking-tight text-navy whitespace-nowrap">
              Lex Polaris
            </span>
            <span className="hidden sm:inline text-[9px] md:text-[10px] tracking-[0.26em] uppercase text-navy/50 font-sans whitespace-nowrap mt-1">
              Advocates &amp; Solicitors
            </span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav
          className="hidden lg:flex items-center gap-7 xl:gap-9"
          data-testid="header-nav-desktop"
        >
          {mainNav.map((item) =>
            item.hasMega ? (
              <div
                key={item.to}
                className="relative"
                onMouseEnter={() => setMegaOpen(true)}
                onMouseLeave={() => setMegaOpen(false)}
              >
                <NavLink
                  to={item.to}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-1 text-[12px] xl:text-[13px] tracking-[0.16em] xl:tracking-[0.18em] uppercase font-medium py-2 transition-colors whitespace-nowrap ${
                      isActive ? "text-navy" : "text-charcoal/80 hover:text-navy"
                    }`
                  }
                >
                  {item.label}
                  <ChevronDown size={14} strokeWidth={2} />
                </NavLink>
                {megaOpen && (
                  <div
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[820px]"
                    data-testid="expertise-mega-menu"
                  >
                    <div className="bg-white border border-soft-grey shadow-2xl p-8 grid grid-cols-2 gap-x-8 gap-y-1">
                      {services.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/expertise/${s.slug}`}
                          data-testid={`mega-link-${s.slug}`}
                          className="group flex items-start gap-3 py-3 px-3 hover:bg-ivory transition-colors"
                        >
                          <span className="text-gold font-serif text-sm pt-1">
                            {String(services.indexOf(s) + 1).padStart(2, "0")}
                          </span>
                          <div>
                            <div className="font-serif text-base text-navy group-hover:text-gold transition-colors">
                              {s.title}
                            </div>
                            <div className="text-[12px] text-charcoal/60 mt-0.5 leading-snug max-w-[280px]">
                              {s.short}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[12px] xl:text-[13px] tracking-[0.16em] xl:tracking-[0.18em] uppercase font-medium py-2 transition-colors whitespace-nowrap ${
                    isActive ? "text-navy" : "text-charcoal/80 hover:text-navy"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        {/* CTA + Mobile toggle */}
        <div className="flex items-center gap-2 md:gap-3">
          <a
            href="https://wa.me/919810899124?text=Hello%20Lex%20Polaris%20Team%2C%20I%20would%20like%20to%20discuss%20a%20legal%20matter."
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-btn"
            aria-label="Chat with Lex Polaris on WhatsApp"
            className="hidden sm:inline-flex items-center justify-center w-11 h-11 border border-navy/20 text-navy hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-colors"
          >
            <MessageCircle size={17} strokeWidth={1.75} />
          </a>
          <Link
            to="/contact"
            data-testid="header-cta-book"
            className="hidden md:inline-flex items-center bg-navy text-ivory px-5 xl:px-6 py-3 xl:py-3.5 text-[11px] xl:text-[12px] tracking-[0.18em] xl:tracking-[0.2em] uppercase font-medium hover:bg-deep-ink transition-colors whitespace-nowrap"
          >
            Book a Consultation
          </Link>
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden p-2 -mr-2 text-navy"
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="lg:hidden bg-ivory border-t border-soft-grey"
          data-testid="mobile-menu-drawer"
        >
          <div className="container-px py-6 flex flex-col">
            {mainNav.map((item) =>
              item.hasMega ? (
                <div key={item.to} className="border-b border-soft-grey/70">
                  <button
                    type="button"
                    data-testid="mobile-expertise-toggle"
                    onClick={() => setMobileExpertiseOpen((v) => !v)}
                    className="w-full flex items-center justify-between py-4 text-sm tracking-[0.18em] uppercase font-medium text-charcoal"
                  >
                    {item.label}
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${mobileExpertiseOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {mobileExpertiseOpen && (
                    <div className="pb-4 pl-1 flex flex-col gap-2.5">
                      <Link
                        to="/expertise"
                        data-testid="mobile-expertise-hub-link"
                        className="text-sm text-navy font-medium"
                      >
                        All Expertise
                      </Link>
                      {services.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/expertise/${s.slug}`}
                          data-testid={`mobile-mega-link-${s.slug}`}
                          className="text-[13px] text-charcoal/75 hover:text-navy"
                        >
                          {s.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `py-4 text-sm tracking-[0.18em] uppercase font-medium border-b border-soft-grey/70 ${
                      isActive ? "text-navy" : "text-charcoal/85"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
            <Link
              to="/contact"
              data-testid="mobile-cta-book"
              className="btn-primary mt-6"
            >
              Book a Consultation
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
